import { useState, useRef, useEffect, useMemo } from 'react'

// A button that opens a list of checkboxes with a search box. `values` is what
// is chosen (empty = no filter, shown as `allLabel`). `options` are
// { value, label, detail? } (detail: a small right-hand note, e.g. a count).
// Applies as you click; "only" keeps just that one.
export default function MultiSelectDropdown({ label, values, options, onChange, allLabel = 'Todos', searchPlaceholder = 'Buscar…', clearLabel = 'Quitar filtro', onlyLabel = 'solo', emptyLabel = 'Sin opciones' }) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const ref = useRef(null)
  const active = values.length > 0
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase()
    return s ? options.filter(o => o.label.toLowerCase().includes(s)) : options
  }, [options, q])
  const summary = !active ? allLabel : values.length === 1 ? (options.find(o => o.value === values[0])?.label || values[0]) : `${values.length}`

  useEffect(() => {
    if (!open) return
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey) }
  }, [open])

  const toggle = (v) => onChange(values.includes(v) ? values.filter(x => x !== v) : [...values, v])

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button" onClick={() => { setOpen(o => !o); setQ('') }} aria-expanded={open} aria-haspopup="true"
        className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${active ? 'border-gray-800 bg-gray-800 text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}
      >
        <span>{label}:</span>
        <span className={`max-w-[10rem] truncate ${active ? '' : 'text-gray-500'}`}>{summary}</span>
        <span aria-hidden="true" className="text-[10px]">▾</span>
      </button>
      {open && (
        <div className="absolute z-30 mt-1 left-0 w-72 max-w-[90vw] bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder={searchPlaceholder} aria-label={searchPlaceholder}
              className="w-full border border-gray-200 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200" />
          </div>
          <ul className="max-h-72 overflow-auto py-1">
            {shown.length === 0 && <li className="px-3 py-2 text-xs text-gray-400">{emptyLabel}</li>}
            {shown.map(o => (
              <li key={o.value} className="group flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50">
                <label className="flex flex-1 items-center gap-2 min-w-0 cursor-pointer text-sm text-gray-800">
                  <input type="checkbox" className="accent-gray-900" checked={values.includes(o.value)} onChange={() => toggle(o.value)} />
                  <span className="truncate" title={o.label}>{o.label}</span>
                </label>
                {o.detail && <span className="text-xs text-gray-400 whitespace-nowrap">{o.detail}</span>}
                <button type="button" onClick={() => onChange([o.value])}
                  className="text-[11px] text-indigo-600 hover:underline opacity-0 group-hover:opacity-100 focus:opacity-100">{onlyLabel}</button>
              </li>
            ))}
          </ul>
          {active && (
            <div className="border-t border-gray-100 p-1.5">
              <button type="button" onClick={() => onChange([])} className="w-full text-xs text-gray-600 hover:text-gray-900 py-1">{clearLabel}</button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
